/**
 * ============================================================================
 * DeactivationBlockedModal - Shows why a court cannot be deactivated
 * ============================================================================
 *
 * Displays a user-friendly modal when court deactivation is blocked due to:
 * - Existing future bookings (shows list with dates/times)
 * - Active recurring bookings (shows list with day/times)
 * - Includes "Go to Bookings" button for quick navigation
 *
 * ============================================================================
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, Calendar, RefreshCw, Info, User, Clock, ExternalLink, ChevronRight } from 'lucide-react';
import Modal from '../ui/Modal';

// Types matching the API response
export interface BlockingBooking {
  id: number;
  reference: string;
  date?: string; // For one-time bookings
  day_of_week?: string; // For recurring bookings
  start_time: string | null;
  end_time: string | null;
  customer_name: string;
  status?: string;
  source?: string;
  frequency?: string; // For recurring bookings
  remaining_sessions?: number; // For recurring bookings
}

export interface DeactivationIssue {
  type: 'future_bookings' | 'recurring_bookings';
  count: number;
  message: string;
  bookings?: BlockingBooking[];
}

export interface DeactivationBlockedDetails {
  issues: DeactivationIssue[];
  action_required: string;
}

export interface DeactivationBlockedData {
  courtName: string;
  message: string;
  blockingReason: string;
  details: DeactivationBlockedDetails;
}

interface DeactivationBlockedModalProps {
  isOpen: boolean;
  data: DeactivationBlockedData | null;
  onClose: () => void;
}

/**
 * Format date for display (e.g., "Mon, Jan 20")
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format time for display (e.g., "2:00 PM")
 */
function formatTime(timeString: string | null | undefined): string {
  if (!timeString) return '--';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Capitalize first letter of each word
 */
function capitalizeWords(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Modal showing why court deactivation is blocked
 */
export function DeactivationBlockedModal({
  isOpen,
  data,
  onClose,
}: DeactivationBlockedModalProps) {
  const navigate = useNavigate();

  if (!data) return null;

  const { courtName, details } = data;
  const { issues, action_required } = details;

  // Find each issue type
  const futureBookingsIssue = issues.find(i => i.type === 'future_bookings');
  const recurringBookingsIssue = issues.find(i => i.type === 'recurring_bookings');

  // Calculate totals
  const totalBookings = issues.reduce((sum, issue) => sum + issue.count, 0);

  // Handle navigation to bookings
  const handleGoToBookings = () => {
    onClose();
    navigate('/bookings');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" animate={false}>
        {/* Header - Warning Style */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Cannot Deactivate Court</h2>
                <p className="text-sm text-white/80">{courtName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Summary Card */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  This court has <span className="font-bold">{totalBookings} active booking{totalBookings !== 1 ? 's' : ''}</span> that must be cancelled first.
                </p>
              </div>
            </div>
          </div>

          {/* Future Bookings Section */}
          {futureBookingsIssue && futureBookingsIssue.count > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {futureBookingsIssue.count} Upcoming Booking{futureBookingsIssue.count !== 1 ? 's' : ''}
                </h3>
              </div>

              {/* Bookings List */}
              <div className="space-y-2">
                {futureBookingsIssue.bookings?.map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-blue-50 border border-blue-100 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                            {booking.reference}
                          </span>
                          {booking.source === 'manual' && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                              Walk-in
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="truncate">{booking.customer_name}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-sm font-semibold text-gray-900">
                          {booking.date && formatDate(booking.date)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Show more indicator */}
                {futureBookingsIssue.count > 5 && (
                  <p className="text-xs text-gray-500 text-center py-1">
                    +{futureBookingsIssue.count - 5} more booking{futureBookingsIssue.count - 5 !== 1 ? 's' : ''}...
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Recurring Bookings Section */}
          {recurringBookingsIssue && recurringBookingsIssue.count > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-purple-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {recurringBookingsIssue.count} Active Recurring Booking{recurringBookingsIssue.count !== 1 ? 's' : ''}
                </h3>
              </div>

              {/* Recurring Bookings List */}
              <div className="space-y-2">
                {recurringBookingsIssue.bookings?.map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-purple-50 border border-purple-100 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                            {booking.reference}
                          </span>
                          {booking.frequency && (
                            <span className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded capitalize">
                              {booking.frequency}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="truncate">{booking.customer_name}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-sm font-semibold text-gray-900">
                          {booking.day_of_week && capitalizeWords(booking.day_of_week)}s
                        </p>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                        </div>
                        {booking.remaining_sessions !== undefined && (
                          <p className="text-xs text-purple-600 mt-0.5">
                            {booking.remaining_sessions} sessions left
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Show more indicator */}
                {recurringBookingsIssue.count > 5 && (
                  <p className="text-xs text-gray-500 text-center py-1">
                    +{recurringBookingsIssue.count - 5} more recurring booking{recurringBookingsIssue.count - 5 !== 1 ? 's' : ''}...
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Required */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">What to do:</h4>
            <p className="text-sm text-gray-600">{action_required}</p>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex-shrink-0 space-y-3">
          {/* Go to Bookings Button */}
          <button
            onClick={handleGoToBookings}
            className="w-full px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <span>Go to Bookings</span>
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
    </Modal>
  );
}
